import { gsap } from "gsap";

export function MobileMenuButton({
  controls = "mobile-navigation",
  label = "MENU",
} = {}) {
  const button = document.createElement("button");

  button.className = "mobile-nav-btn";
  button.type = "button";
  button.setAttribute("aria-expanded", "false");
  button.setAttribute("aria-label", "Open menu");
  button.setAttribute("aria-controls", controls);

  button.innerHTML = `
    <span class="menu-label">
      <span class="mobile-nav-label">${label}</span>
    </span>

    <span class="menu-icon" aria-hidden="true">
      <span class="menu-lineTop"></span>
      <span class="menu-lineBottom"></span>
    </span>
  `;

  return button;
}

export function initMobileMenuButton(mobileButton, mobileNav) {
  const topLine = mobileButton.querySelector(".menu-lineTop");
  const bottomLine = mobileButton.querySelector(".menu-lineBottom");
  const navLinks = gsap.utils.toArray(".mobile-navlink", mobileNav);
  const navDividers = gsap.utils.toArray(".nav-divider", mobileNav);

  if (!topLine || !bottomLine || !navLinks.length || !navDividers.length) {
    return;
  }

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  gsap.set([topLine, bottomLine], { transformOrigin: "50% 50%" });
  gsap.set(mobileNav, { autoAlpha: 0, pointerEvents: "none" });
  gsap.set(navLinks, {
    autoAlpha: 0,
    scale: 0.96,
    transformOrigin: "0% 50%",
  });
  gsap.set(navDividers, {
    scaleX: 0,
    transformOrigin: "0% 50%",
  });

  const menuTimeline = gsap
    .timeline({
      paused: true,
      onReverseComplete: () => {
        gsap.set(mobileNav, { autoAlpha: 0 });
        document.body.classList.remove("nav-is-open");
      },
      defaults: {
        duration: 0.3,
        ease: "sine.inOut",
      },
    })
    .to(topLine, { y: 3.5, rotation: 45 }, 0)
    .to(bottomLine, { y: -3.5, rotation: -45 }, 0.035)
    .to(
      navDividers,
      {
        scaleX: 1,
        duration: 0.5,
        stagger: 0.075,
        ease: "power2.out",
      },
      0.08,
    )
    .to(
      navLinks,
      {
        autoAlpha: 1,
        scale: 1,
        duration: 0.35,
        stagger: 0.065,
        ease: "power2.out",
      },
      0.16,
    );

  const setMenuState = (willOpen) => {
    const isOpen =
      mobileButton.getAttribute("aria-expanded") === "true";

    if (willOpen === isOpen) {
      return;
    }

    mobileButton.setAttribute("aria-expanded", String(willOpen));
    mobileButton.setAttribute(
      "aria-label",
      willOpen ? "Close menu" : "Open menu",
    );
    mobileNav.setAttribute("aria-hidden", String(!willOpen));
    mobileNav.toggleAttribute("inert", !willOpen);

    if (willOpen) {
      document.body.classList.add("nav-is-open");
      gsap.set(mobileNav, {
        autoAlpha: 1,
        pointerEvents: "auto",
      });
    } else {
      gsap.set(mobileNav, { pointerEvents: "none" });
    }

    if (prefersReducedMotion) {
      menuTimeline.progress(willOpen ? 1 : 0).pause();

      if (!willOpen) {
        gsap.set(mobileNav, { autoAlpha: 0 });
        document.body.classList.remove("nav-is-open");
      }

      return;
    }

    if (willOpen) {
      menuTimeline.play();
    } else {
      menuTimeline.reverse();
    }
  };

  mobileButton.addEventListener("click", () => {
    const willOpen =
      mobileButton.getAttribute("aria-expanded") !== "true";

    setMenuState(willOpen);
  });

  mobileNav.addEventListener("click", (event) => {
    if (event.target.closest(".mobile-navlink")) {
      setMenuState(false);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (
      event.key === "Escape" &&
      mobileButton.getAttribute("aria-expanded") === "true"
    ) {
      setMenuState(false);
      mobileButton.focus();
    }
  });

  const mobileBreakpoint = window.matchMedia("(max-width: 720px)");

  mobileBreakpoint.addEventListener("change", (event) => {
    if (
      !event.matches &&
      mobileButton.getAttribute("aria-expanded") === "true"
    ) {
      menuTimeline.progress(0).pause();
      mobileButton.setAttribute("aria-expanded", "false");
      mobileButton.setAttribute("aria-label", "Open menu");
      mobileNav.setAttribute("aria-hidden", "true");
      mobileNav.setAttribute("inert", "");
      gsap.set(mobileNav, {
        autoAlpha: 0,
        pointerEvents: "none",
      });
      document.body.classList.remove("nav-is-open");
    }
  });
}
